import { Dispatch, MiddlewareAPI } from 'redux';
import ApiUrls from '../../../../../constants/ApiUrls';
import MiddlewareEntities from '../../../../../constants/MiddlewareEntities';
import UrlService from '../../../../../services/UrlService';
import { pushSubmitOrderToAnalyticsAction } from '../../../analytics/actions';
import { apiRequest } from '../../../api/actions';
import { setError, setLoader } from '../../../ui/actions';
import {
  goToNextStepAction,
  goToPreviousStepAction,
  manhattanOrderValidatorAction,
  requestFetchManhattanOrderAction,
  setManhattanCheckoutDataAction,
  setManhattanCustomerInputDataAction,
  setManhattanOrderAction,
  setManhattanOreAction,
} from './actions';
import { IOrder, IOrderSteps, IOrderBasket } from '../../../../../entities/manhattan/order/types';
import { ISessionData } from '../../../sessionData/types';
import { getSessionData } from '../../../sessionData/actions';
import { clearNotifications, createErrorNotification, getNotification } from '../../../notification/actions';
import { AppState } from '../../../rootReducers';
import ErrorCodes from '../../../../../constants/ErrorCodes';
import { TransitionSteps } from '../../../../../constants/TransitionSteps';
import { redirectToAction, setErrorModeAction } from '../../../redirection/actions';
import { getActiveLanguage, prepareUrlForRedirect } from '../../../../../utilites/util';
import CheckoutSteps from '../../../../../constants/CheckoutSteps';
import OrderValidator from '../../../../../validators/order/mahhattan/OrderValidator';
import { OrderFactory } from '../../../../../factories/manhattan/order/OrderFactory';
import { CheckoutDataFactory } from '../../../../../factories/checkout/checkout-data/CheckoutDataFactory';
import { CreditCardsFactory } from '../../../../../factories/manhattan/customerInput/CreditCardsFactory';
import { setSmsFlagsFromOrder } from '../../verification-sms/actions';
import { OrderTypes } from '../../../../../constants/OrderTypes';
import { BundleProductLines } from '../../../../../constants/BundleProductLines';

type ManhattanOrderAPIResponse = {
  data: ManhattanOrder;
};

type ManhattanOrder = IOrder & { session: ISessionData } & { steps: IOrderSteps } & { basket: IOrderBasket[] };

abstract class IManhattanTransitionMiddlewareProxy {

  public static getOrder: (next: Dispatch) => void;
  public static fetchOrder: (next: Dispatch, params: any) => void;
  public static getOrderAPISuccess: (next: Dispatch, store: MiddlewareAPI, data: ManhattanOrder) => void;
  public static getOrderAPIError: (state: AppState, next: Dispatch, payload: any) => void;
  public static submitOrderDataStep: (next: Dispatch, payload: any) => void;
  public static submitOrderDataStepAPISuccess: (
    store: MiddlewareAPI,
    next: Dispatch,
    payload: ManhattanOrderAPIResponse
  ) => void;
  public static submitOrderDataStepAPIError: (next: Dispatch, action: any) => void;
  public static goToNextStep: (state: AppState, store: MiddlewareAPI, next: Dispatch, payload: any) => void;
  public static submitOrderPrevStep: (next: Dispatch, action: any) => void;
  public static submitOrderPrevStepAPISuccess: (next: Dispatch, store: MiddlewareAPI, action: any) => void;
  public static submitOrderPrevStepAPIError: (next: Dispatch, action: any) => void;
  public static goToPreviousStep: (store: MiddlewareAPI) => void;
  public static orderValidator: (next: Dispatch, payload: any) => void;

}

export default class ManhattanTransitionMiddlewareProxy extends IManhattanTransitionMiddlewareProxy {

  public static getOrder(next: Dispatch): void {
    const hash = UrlService.getUrlParameter(window.location.href, 's');

    if (hash) {
      next(requestFetchManhattanOrderAction({ s: hash }));
      return;
    }

    next(requestFetchManhattanOrderAction({}));
  }

  public static fetchOrder(next: Dispatch, params: any): void {
    next(
      apiRequest(
        null,
        'GET',
        `${ApiUrls.FETCH_MANHATTAN_ORDER}`,
        params,
        null,
        MiddlewareEntities.ORDER.MANHATTAN.ORDER,
        {}
      )
    );
    next(setLoader(true, MiddlewareEntities.ORDER.MANHATTAN.ORDER));
  }

  public static getOrderAPISuccess(next: Dispatch, store: MiddlewareAPI, data: ManhattanOrder): void {
    next(manhattanOrderValidatorAction(data));
    next(getSessionData(data.session));
    next(getNotification(data.session.flashMessages, MiddlewareEntities.ORDER.MANHATTAN.ORDER));
    next(setError(false, MiddlewareEntities.ORDER.MANHATTAN.ORDER));
    next(setLoader(false, MiddlewareEntities.ORDER.MANHATTAN.ORDER));
  }

  public static getOrderAPIError(state: AppState, next: Dispatch, payload: any): void {
    const { code, status } = payload.data;
    const { step } = state.manhattanOrder.checkoutData.steps.current;

    if (code === ErrorCodes.ORDER_NOT_FOUND && step === TransitionSteps.COMMON.CONFIRM_ORDER_STATE) {
      window.location.reload();
    }

    next(setLoader(false, MiddlewareEntities.ORDER.MANHATTAN.ORDER));
    next(setError(true, MiddlewareEntities.ORDER.MANHATTAN.ORDER));

    if (status !== 404) {
      next(createErrorNotification(payload.data, MiddlewareEntities.ORDER.MANHATTAN.ORDER));
    }
  }

  public static submitOrderDataStep(next: Dispatch, action: any): void {
    next(
      apiRequest(
        null,
        'PUT',
        `${ApiUrls.ORDER_SUBMIT}`,
        action.params,
        action.payload.data,
        MiddlewareEntities.ORDER.MANHATTAN.NEXT_ORDER_DATA_STEP,
        {}
      )
    );
    next(clearNotifications());
    next(setLoader(true, MiddlewareEntities.ORDER.MANHATTAN.NEXT_ORDER_DATA_STEP));
  }

  public static submitOrderDataStepAPISuccess(
    store: MiddlewareAPI,
    next: Dispatch,
    payload: ManhattanOrderAPIResponse
  ): void {
    next(getSessionData(payload.data.session));
    next(getNotification(payload.data.session.flashMessages, MiddlewareEntities.ORDER.MANHATTAN.NEXT_ORDER_DATA_STEP));
    next(setError(false, MiddlewareEntities.ORDER.MANHATTAN.ORDER));
    next(setLoader(false, MiddlewareEntities.ORDER.MANHATTAN.NEXT_ORDER_DATA_STEP));
    store.dispatch(goToNextStepAction(payload));
  }

  public static submitOrderDataStepAPIError(next: Dispatch, action: any): void {
    const { code } = action.payload.data;
    const { PAK_NOT_FOUND_EXCEPTION, MULTIPLE_LOCATIONS_FOUND } = ErrorCodes;
    const errorCodesThatTriggerRetry = [PAK_NOT_FOUND_EXCEPTION, MULTIPLE_LOCATIONS_FOUND];

    if (errorCodesThatTriggerRetry.includes(code)) {
      next(
        apiRequest(
          null,
          'GET',
          `${ApiUrls.ORDER_SUBMIT}`,
          action.params,
          null,
          MiddlewareEntities.ORDER.MANHATTAN.NEXT_ORDER_DATA_STEP,
          {}
        )
      );
    } else {
      next(setLoader(false, MiddlewareEntities.ORDER.MANHATTAN.NEXT_ORDER_DATA_STEP));
    }

    next(setError(true, MiddlewareEntities.ORDER.MANHATTAN.NEXT_ORDER_DATA_STEP));
    next(createErrorNotification(action.payload.data, MiddlewareEntities.ORDER.MANHATTAN.NEXT_ORDER_DATA_STEP));
  }

  public static goToNextStep(state: AppState, store: MiddlewareAPI, next: Dispatch, payload: any) {
    const language = getActiveLanguage();
    const { step } = state.manhattanOrder.checkoutData.steps.current;
    const { CONFIRM_ORDER_STATE } = TransitionSteps.COMMON;
    if (step === CONFIRM_ORDER_STATE && payload.data.transaction) {
      if (payload.data.nextSteps[0]?.type !== BundleProductLines.PURE_OTT) {
        store.dispatch(pushSubmitOrderToAnalyticsAction());
      }
      next(
        redirectToAction(
          `${prepareUrlForRedirect(CheckoutSteps[language].transactionOverview)}/${payload.data.transaction}`
        )
      );
      return;
    }

    store.dispatch(requestFetchManhattanOrderAction({}));
  }

  public static submitOrderPrevStep(next: Dispatch, action: any): void {
    next(
      apiRequest(
        null,
        'POST',
        `${ApiUrls.EDIT_STEP}`,
        action.params,
        action.payload.data,
        MiddlewareEntities.ORDER.MANHATTAN.PREVIOUS_ORDER_DATA_STEP,
        {}
      )
    );
    next(setLoader(true, MiddlewareEntities.ORDER.MANHATTAN.PREVIOUS_ORDER_DATA_STEP));
  }

  public static submitOrderPrevStepAPISuccess(next: Dispatch, store: MiddlewareAPI, action: any): void {
    next(getSessionData(action.payload.data.session));
    next(
      getNotification(
        action.payload.data.session.flashMessages,
        MiddlewareEntities.ORDER.MANHATTAN.PREVIOUS_ORDER_DATA_STEP
      )
    );
    next(setLoader(false, MiddlewareEntities.ORDER.MANHATTAN.PREVIOUS_ORDER_DATA_STEP));
    store.dispatch(goToPreviousStepAction(action.payload.meta.data.step));
  }

  public static submitOrderPrevStepAPIError(next: Dispatch, action: any): void {
    next(setLoader(false, MiddlewareEntities.ORDER.MANHATTAN.ORDER));
    next(createErrorNotification(action.payload.data, MiddlewareEntities.ORDER.MANHATTAN.ORDER));
  }

  public static goToPreviousStep(store: MiddlewareAPI): void {
    store.dispatch(requestFetchManhattanOrderAction({}));
  }

  public static orderValidator(next: Dispatch, payload: ManhattanOrder): void {
    const currentStep = payload.steps.current.step;
    const { CHOOSE_NUMBER_STATE, PERSONAL_DATA_STATE } = TransitionSteps.TELCO;

    if (!OrderValidator.validate(payload)) {
      next(setErrorModeAction());
      return;
    }

    next(setManhattanOrderAction(OrderFactory.create(payload, payload.steps.current.type)));
    next(setManhattanCheckoutDataAction(CheckoutDataFactory.create(payload.steps)));

    if (
      [TransitionSteps.OTT.PAYMENT_OPTION_STATE, TransitionSteps.TELCO.CONFIRM_ORDER_STATE].includes(
        payload.steps.current.step
      )
    ) {
      const defaultCreditCard = payload.steps.current.data.creditCards?.find((item: any) => item.isDefault);
      if (defaultCreditCard && payload.steps.current.step === TransitionSteps.TELCO.CONFIRM_ORDER_STATE) {
        next(setManhattanCustomerInputDataAction(CreditCardsFactory.create(defaultCreditCard)));
      }
      if (TransitionSteps.OTT.PAYMENT_OPTION_STATE === payload.steps.current.step) {
        const contactPhone = payload.steps.current.data.contactPhone;
        next(
          setManhattanCustomerInputDataAction({
            creditCard: defaultCreditCard,
            phoneNumber: contactPhone,
          })
        );
      }
    }

    if (payload.basket[0].ore) {
      next(setManhattanOreAction(payload.basket[0].ore));
    }

    if (
      payload.steps.current.type === OrderTypes.TELCO &&
      [CHOOSE_NUMBER_STATE, PERSONAL_DATA_STATE].includes(currentStep)
    ) {
      next(
        setSmsFlagsFromOrder({
          smsVerificationNeeded: payload.steps.current.data.smsVerificationNeeded,
          smsVerificationSent: payload.steps.current.data.smsVerificationSent,
          smsVerificationConfirmed: payload.steps.current.data.smsVerificationConfirmed,
        })
      );
    }
  }

}
